/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { SortOption } from '@/lib/search/sort-options';
import BgMobileSortOverlay from './bg-mobile-sort-overlay';

/**
 * BG's mobile filter/sort affordance — a two-segment `Filters │ Sort` pill that
 * rides the bottom of the viewport over the results grid.
 *
 * A BG BAND, AND IT REPLACES TWO THINGS AT ONCE. Before it, BG's SRP rendered a
 * single in-flow grey `FILTERS` button at the top-left of the results column and
 * NO sort control at any width below `md` — live BG has neither. NM is
 * untouched: its mobile affordance is `SearchStickyBar` (Filter | count | Sort,
 * at the TOP of the results column), a different element in a different place,
 * behind its own `SearchUx.mobileStickyBar` flag.
 *
 * STRUCTURE, read in document order from
 * `.reference/captures/bg-search-m-375/App.tsx` at :2507 — the block that
 * immediately follows the last tile of the grid:
 *
 *   div.sticky.w-full.bottom-5.mb-[30px].z-[3]
 *     div.border.flex.ml-auto.mr-auto.w-[267px].bg-white.border-black
 *        .pt-[10px].pr-0.pb-[10px].pl-0
 *       button.border-r.text-center.w-[50%].bg-black/0.border-black
 *              .pt-0.pr-3.pb-0.pl-3.shrink-[0]
 *         span.text-center.text-[13px].tracking-[0.4px].leading-[17.94px]
 *                                                                "Filters"
 *       div.w-[50%].pt-0.pr-3.pb-0.pl-3.shrink-[0]
 *         button.relative.text-center.w-full.h-[26px].bg-black/0
 *                .pt-0.pr-[15px].pb-0.pl-[15px]                  "Sort"
 *
 * THE PILL IS `sticky`, NOT `fixed`, and that is live's own choice rather than
 * an approximation of one. It sits in the grid's flow with `bottom: 20px`, so it
 * rides up from below the tiles as the shopper scrolls instead of covering them
 * from first paint. Both owner captures catch it in that state
 * (`.reference/live-shots/bg-search-390.png`, `bg-plp-390.png`). Making it
 * `fixed` would put it over the masthead at the top of the page, which neither
 * capture shows.
 *
 * THE DIVIDER IS THE LEFT SEGMENT'S `border-r`, not a separate rule element —
 * which is why it spans only the pill's inner height and not its padding.
 *
 * WHY THE SORT SEGMENT IS STILL A NATIVE `<select>` UNDERNEATH, AND THE DESKTOP
 * CONTROL IS NOT. This used to say "the same reason `SortMenu` gives" — that
 * reason is gone. `SortMenu` draws live's real panel now, because live's shipped
 * stylesheet declares the desktop `.sort-container` outright and no capture was
 * needed. The mobile menu is NOT that panel and is not covered by those values.
 *
 * Live's mobile Sort opens a MODAL. Read in the mobile dump
 * `.reference/dumps/bg-search-m-440/mob-search-440.html`:
 * `div.mobile-options-container.hide-on-desktop.hide-on-tablet` holds a
 * `Filters | Sort` row whose Sort is
 * `button.mobile-overlay__button[aria-label=Sort] > label`, and that opens
 * `.mobile-overlay__pane` — `position:fixed`, inset 0, `background:#fff`,
 * `z-index:1010`, a centred uppercase `h2` header and a 30x30 close control at
 * the leading edge. Building it means a focus trap, a scroll lock and a header
 * this storefront has no counterpart for, and our pill is already our own
 * arrangement rather than live's row — so the anchoring would be invented. The
 * measured overlay values are recorded under MOBILE in
 * `theme/tokens/brand-bg/search.css`; until it is built the visible affordance
 * is live's (the word "Sort", sized and placed exactly as live's button is) and
 * the menu is the platform's, which is accessible for free.
 */
export default function BgFilterSortBar({
    onToggleFilters,
    filtersOpen,
    filtersPanelId,
    filtersTriggerRef,
    serverSortOptions,
    fallbackSortOptions,
}: {
    onToggleFilters: () => void;
    filtersOpen: boolean;
    /** `aria-controls` target — the panel the Filters segment discloses. */
    filtersPanelId?: string;
    /**
     * Handed down so the filter pane can restore focus HERE when it closes.
     * Radix restores focus to whatever held it before the pane opened, and on
     * macOS and on touch a click does not focus a `<button>` — so at the moment
     * of opening `document.activeElement` is the body and there is nothing to
     * restore TO. The pane intercepts `onCloseAutoFocus` and focuses this ref by
     * hand; without the ref the shopper's place in the page is simply lost.
     * Optional so the bar's existing mounts and its own tests are unaffected.
     */
    filtersTriggerRef?: RefObject<HTMLButtonElement | null>;
    serverSortOptions?: Array<{ id?: string; label?: string }>;
    /** The brand's curated list — named by the caller, never defaulted. */
    fallbackSortOptions: readonly SortOption[];
}): ReactElement {
    const { t } = useTranslation('search');

    return (
        <div
            data-testid="bg-filter-sort-bar"
            className="sticky bottom-(--bg-srp-pill-bottom-inset) z-(--bg-srp-pill-layer) mb-(--bg-srp-pill-space-bottom) w-full">
            <div
                className={cn(
                    'mx-auto flex w-(--bg-srp-pill-width) items-stretch',
                    'bg-(--bg-srp-pill-background) border-(length:--bg-srp-pill-border-width)',
                    '[border-color:var(--bg-srp-pill-border-color)]',
                    'py-(--bg-srp-pill-pad-block)',
                    'text-(length:--bg-srp-pill-font-size) leading-(--bg-srp-pill-line-height) tracking-(--bg-srp-pill-letter-spacing)'
                )}>
                <button
                    type="button"
                    data-testid="bg-filter-sort-bar-filters"
                    ref={filtersTriggerRef}
                    onClick={onToggleFilters}
                    aria-expanded={filtersOpen}
                    aria-controls={filtersPanelId}
                    className="w-1/2 shrink-0 cursor-pointer border-r-(length:--bg-srp-pill-divider-width) [border-right-color:var(--bg-srp-pill-divider-color)] px-(--bg-srp-pill-segment-pad-inline) text-center focus-ring-button">
                    {t('bgFiltersSegment', { defaultValue: 'Filters' })}
                </button>
                <div className="relative w-1/2 shrink-0 px-(--bg-srp-pill-segment-pad-inline)">
                    {/* The Sort segment and the full-screen pane it opens are
                        ONE component: the pane's selected state is the pill's
                        state, and splitting them put the URL plumbing in two
                        places. See bg-mobile-sort-overlay.tsx for live's
                        `.mobile-overlay__pane` spec and its two sources. */}
                    <BgMobileSortOverlay serverOptions={serverSortOptions} fallbackOptions={fallbackSortOptions} />
                </div>
            </div>
        </div>
    );
}
